/**
 * Importer le classeur d'employés — chunk 9.BK.2.
 *
 * ## Trois moments, et un seul écrit
 *
 * ```
 *   choisir le fichier  →  revoir et corriger  →  remplacer
 *        (dépôt)            (rien n'a bougé)      (le seul écrit)
 * ```
 *
 * La revue ne touche à rien. Tant que le conseiller n'a pas confirmé, l'ancien
 * effectif est intact — et s'il ferme ce dialogue, il n'y a rien à défaire.
 *
 * ## Ce que cet écran refuse de faire
 *
 * **Il ne rejuge rien.** Les manques par ligne, les colonnes reconnues, les
 * anomalies et l'annualisation proposée viennent du serveur. Une seconde
 * lecture des règles ici finirait par diverger de celle qui décide vraiment.
 *
 * **Il n'écrit pas la suggestion à la place du conseiller.** Quand une
 * rémunération est horaire, l'annualisation s'affiche en gris, à côté de la
 * ligne. Elle n'entre pas dans le salaire annuel : une valeur calculée qui
 * s'installe silencieusement dans un champ déclaratif devient indiscernable
 * d'une valeur déclarée dès le lendemain.
 *
 * **Il dit combien de lignes disparaîtront** avant de les faire disparaître.
 *
 * ## Corriger ici plutôt qu'après
 *
 * Une date écrite « 12 avril 85 » arrive vide, avec son anomalie. La corriger
 * dans la revue coûte un champ ; la corriger après l'import coûte de retrouver
 * la ligne dans une liste de deux cents. C'est ce qui fait de cet écran une
 * revue **et** une correction.
 *
 * Une correction ne fait pas rejuger la ligne par le navigateur : les manques
 * affichés restent ceux du serveur, marqués « avant correction », et le verdict
 * définitif revient de la liste après l'import. L'écran ne rejuge rien, même
 * quand ce serait facile.
 */

import { Schemas } from "@kollectivia/shared";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getApiErrorMessage } from "../../../lib/apiErrorMessage";
import {
  useCensusImport,
  type CensusImportPreview,
  type CensusImportRow,
} from "../../../lib/queries/census";
import { useCompleteFileUpload, useInitiateFileUpload } from "../../../lib/queries/files";
import { beginTenantRequest } from "../../../lib/tenantScope";
import { sha256OfFile, uploadToBucket } from "../../../lib/uploadPdf";
import { CoverageChoice } from "../../public-census/_parts/CoverageChoice";

import { DownloadCensusTemplate } from "./DownloadCensusTemplate";
import { ReceivedCensusWorkbooks } from "./ReceivedCensusWorkbooks";

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

type Etape =
  | { readonly kind: "choix" }
  | { readonly kind: "depot" }
  | { readonly kind: "analyse" }
  /**
   * `erreur` accompagne la revue au lieu de la remplacer.
   *
   * Un échec d'application ne doit pas coûter au conseiller les corrections
   * qu'il vient de saisir : il doit lire ce qui a échoué **et** garder sous les
   * yeux ce qu'il a relu, pour corriger et réessayer. Écraser la revue par un
   * message laissait un dialogue quasi vide, avec « Annuler » pour seule issue.
   */
  | { readonly kind: "revue"; readonly apercu: CensusImportPreview; readonly erreur?: string }
  | { readonly kind: "application"; readonly apercu: CensusImportPreview }
  | { readonly kind: "echec"; readonly message: string };

export function ImportCensusDialog({
  projectId,
  censusId,
  onFermer,
  onImporte,
}: {
  readonly projectId: string;
  readonly censusId: string;
  readonly onFermer: () => void;
  readonly onImporte: (comptes: { rows_applied: number; rows_replaced: number }) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const initier = useInitiateFileUpload();
  const confirmer = useCompleteFileUpload();
  const imports = useCensusImport(projectId);

  const [etape, setEtape] = useState<Etape>({ kind: "choix" });
  /** Les lignes que le conseiller retire de l'import, par numéro de ligne. */
  const [ecartees, setEcartees] = useState<ReadonlySet<number>>(new Set());
  /** Les valeurs que le conseiller a reprises, par numéro de ligne du classeur. */
  const [corrections, setCorrections] = useState<
    Readonly<Record<number, Record<string, string | number | null>>>
  >({});

  // Échapper ferme — le réflexe attendu de tout dialogue. Il n'y a rien à
  // perdre : tant que le remplacement n'est pas confirmé, rien n'a été écrit.
  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === "Escape") onFermer();
    };
    document.addEventListener("keydown", surTouche);
    return () => document.removeEventListener("keydown", surTouche);
  }, [onFermer]);

  async function deposerPuisAnalyser(fichier: File): Promise<void> {
    const operation = beginTenantRequest(true);
    setEtape({ kind: "depot" });
    try {
      const sha256 = await sha256OfFile(fichier);
      operation.assertCurrent();
      const { file, upload } = await initier.mutateAsync({
        project_id: projectId,
        kind: "census_source",
        file_name: fichier.name,
        size_bytes: fichier.size,
        mime_type: XLSX_MIME,
        sha256,
      } as never);
      operation.assertCurrent();
      await uploadToBucket({
        url: upload.url,
        headers: upload.headers,
        body: fichier,
        signal: operation.signal,
      });
      operation.assertCurrent();
      await confirmer.mutateAsync({ fileId: file.id, input: { sha256 } });

      operation.assertCurrent();
      setEtape({ kind: "analyse" });
      const apercu = await imports.analyser.mutateAsync({ censusId, fileId: file.id });
      operation.assertCurrent();
      setEcartees(new Set());
      setCorrections({});
      setEtape({ kind: "revue", apercu });
    } catch (err) {
      // Ici il n'y a pas encore de revue à préserver : le dialogue montre
      // l'échec et le conseiller redépose un fichier.
      setEtape({ kind: "echec", message: messageDErreur(err, langue(i18n.language), t) });
    } finally {
      operation.finish();
    }
  }

  async function analyserRecu(fileId: string): Promise<void> {
    setEtape({ kind: "analyse" });
    try {
      const apercu = await imports.analyser.mutateAsync({ censusId, fileId });
      setEcartees(new Set());
      setCorrections({});
      setEtape({ kind: "revue", apercu });
    } catch (err) {
      setEtape({ kind: "echec", message: messageDErreur(err, langue(i18n.language), t) });
    }
  }

  async function remplacer(apercu: CensusImportPreview): Promise<void> {
    setEtape({ kind: "application", apercu });
    const retenues = apercu.rows.filter((r) => !ecartees.has(r.ligne));
    try {
      const comptes = await imports.appliquer.mutateAsync({
        censusId,
        corps: {
          coverage_review_revision: apercu.coverage_review_revision,
          file_id: apercu.file_id,
          sheet_name: apercu.sheet_name,
          header_row: apercu.header_row,
          rows_detected: apercu.rows_detected,
          anomalies_count: apercu.anomalies.length,
          anomaly_kinds: [...new Set(apercu.anomalies.map((a) => a.motif))],
          unknown_columns: [...apercu.unknown_columns],
          rows: retenues.map((r) => construireLigne(r, corrections[r.ligne])),
        },
      });
      onImporte(comptes);
    } catch (err) {
      // On REVIENT à la revue, avec l'erreur — jamais un dialogue vide.
      setEtape({
        kind: "revue",
        apercu,
        erreur: messageDErreur(err, langue(i18n.language), t),
      });
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="census-import-title"
      className="fixed inset-0 z-50 flex items-center justify-center overscroll-contain bg-slate-900/40 p-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <header className="border-b border-slate-100 px-5 py-4">
          <h4 id="census-import-title" className="text-sm font-semibold text-slate-900">
            {t("census.import.title")}
          </h4>
          <p className="mt-1 text-xs text-slate-500">{t("census.import.subtitle")}</p>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
          {etape.kind === "choix" ? (
            <ReceivedCensusWorkbooks censusId={censusId} onReview={(id) => void analyserRecu(id)} />
          ) : null}
          {etape.kind === "choix" ? <DownloadCensusTemplate /> : null}
          {etape.kind === "choix" ? <Choix onFichier={deposerPuisAnalyser} /> : null}

          {etape.kind === "depot" || etape.kind === "analyse" ? (
            <p role="status" className="py-8 text-center text-sm text-slate-600">
              {t(etape.kind === "depot" ? "census.import.uploading" : "census.import.analysing")}
            </p>
          ) : null}

          {etape.kind === "revue" && etape.erreur ? (
            <p
              role="alert"
              className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900"
            >
              {etape.erreur}
            </p>
          ) : null}

          {etape.kind === "revue" || etape.kind === "application" ? (
            <Revue
              apercu={etape.apercu}
              ecartees={ecartees}
              corrections={corrections}
              onCorriger={(ligne, champ, valeur) =>
                setCorrections((c) => ({
                  ...c,
                  [ligne]: { ...(c[ligne] ?? {}), [champ]: valeur },
                }))
              }
              onBasculer={(ligne) =>
                setEcartees((s) => {
                  const n = new Set(s);
                  if (n.has(ligne)) n.delete(ligne);
                  else n.add(ligne);
                  return n;
                })
              }
            />
          ) : null}

          {etape.kind === "echec" ? (
            <p role="alert" className="py-8 text-center text-sm text-rose-700">
              {/* Le libellé vient du registre bilingue du serveur : c'est lui
                  qui tient le vocabulaire des refus, pas cet écran. */}
              {etape.message}
            </p>
          ) : null}
        </div>

        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          {etape.kind === "revue" || etape.kind === "application" ? (
            <ResumeRemplacement
              apercu={etape.apercu}
              retenues={etape.apercu.rows.length - ecartees.size}
            />
          ) : null}
          <button
            type="button"
            onClick={onFermer}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("census.import.cancel")}
          </button>
          {etape.kind === "revue" || etape.kind === "application" ? (
            <button
              type="button"
              disabled={
                etape.kind === "application" || etape.apercu.rows.length - ecartees.size === 0
              }
              onClick={() => void remplacer(etape.apercu)}
              className="focus-visible:ring-brand-500 touch-manipulation rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {etape.kind === "application"
                ? t("census.import.applying")
                : t("census.import.replace")}
            </button>
          ) : null}
        </footer>
      </div>
    </div>
  );
}

/** Le choix du fichier. Rien n'est envoyé tant qu'il n'est pas choisi. */
function Choix({ onFichier }: { onFichier: (f: File) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="py-6">
      <label htmlFor="census-import-file" className="block text-sm font-medium text-slate-700">
        {t("census.import.file_label")}
      </label>
      <input
        id="census-import-file"
        // Le seul champ du dialogue, et la seule chose à y faire. Le focus
        // automatique évite un aller-retour à la souris sans jamais capturer
        // le clavier ailleurs.
        autoFocus
        type="file"
        accept=".xlsx"
        className="focus-visible:ring-brand-500 mt-2 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFichier(f);
        }}
      />
      <p className="mt-2 text-xs text-slate-500">{t("census.import.file_hint")}</p>
    </div>
  );
}

/** Ce que le remplacement fera, dit avant de le faire. */
function ResumeRemplacement({
  apercu,
  retenues,
}: {
  apercu: CensusImportPreview;
  retenues: number;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <p className="mr-auto text-xs text-slate-600">
      {/* i18next ne choisit le pluriel que sur `count` — jamais sur un autre
          nom de variable. Le piège a déjà coûté une clé brute à l'écran. */}
      {t("census.import.summary", { count: retenues, existing: apercu.rows_existing })}
    </p>
  );
}

function Revue({
  apercu,
  ecartees,
  corrections,
  onBasculer,
  onCorriger,
}: {
  apercu: CensusImportPreview;
  ecartees: ReadonlySet<number>;
  corrections: Readonly<Record<number, Record<string, string | number | null>>>;
  onBasculer: (ligne: number) => void;
  onCorriger: (ligne: number, champ: string, valeur: string | number | null) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const anomaliesParLigne = useMemo(() => {
    const m = new Map<number, number>();
    for (const a of apercu.anomalies) m.set(a.ligne, (m.get(a.ligne) ?? 0) + 1);
    return m;
  }, [apercu.anomalies]);

  return (
    <div>
      <dl className="mb-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
        <Fait terme={t("census.import.sheet")} valeur={apercu.sheet_name} />
        <Fait terme={t("census.import.detected")} valeur={String(apercu.rows_detected)} />
        <Fait
          terme={t("census.import.recognised")}
          valeur={String(apercu.columns_recognised.length)}
        />
        <Fait terme={t("census.import.existing")} valeur={String(apercu.rows_existing)} />
      </dl>

      {apercu.unknown_columns.length > 0 ? (
        // Signalées, jamais devinées : une colonne qu'on rattacherait « au plus
        // proche » ferait entrer des salaires dans un champ de notes.
        <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {t("census.import.unknown_columns", { count: apercu.unknown_columns.length })}{" "}
          <span className="font-medium">{apercu.unknown_columns.join(" · ")}</span>
        </p>
      ) : null}

      {(apercu.coverage_choices_existing ?? 0) > 0 ? (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          {t("census.import.coverage_replacement", { count: apercu.coverage_choices_existing })}
        </p>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[48rem] text-left text-xs">
          <thead className="text-slate-500">
            <tr>
              <th scope="col" className="py-1.5 pr-3 font-medium">
                {t("census.import.table_keep")}
              </th>
              <th scope="col" className="py-1.5 pr-3 font-medium">
                {t("census.import.table_line")}
              </th>
              <th scope="col" className="py-1.5 pr-3 font-medium">
                {t("census.broker.table_employee")}
              </th>
              <th scope="col" className="py-1.5 pr-3 font-medium">
                {t("census.broker.table_birth")}
              </th>
              <th scope="col" className="py-1.5 pr-3 font-medium">
                {t("census.broker.table_pay")}
              </th>
              <th scope="col" className="py-1.5 pr-3 font-medium">
                {t("census.import.table_findings")}
              </th>
              <th scope="col" className="py-1.5 font-medium">
                <span className="sr-only">{t("census.import.table_fix")}</span>
              </th>
            </tr>
          </thead>
          <tbody className="text-slate-800">
            {apercu.rows.map((r) => (
              <LigneRevue
                key={r.ligne}
                ligne={r}
                ecartee={ecartees.has(r.ligne)}
                anomalies={anomaliesParLigne.get(r.ligne) ?? 0}
                correction={corrections[r.ligne]}
                onBasculer={onBasculer}
                onCorriger={onCorriger}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Fait({ terme, valeur }: { terme: string; valeur: string }): JSX.Element {
  return (
    <div>
      <dt className="text-slate-500">{terme}</dt>
      <dd className="mt-0.5 break-words font-medium tabular-nums text-slate-900">{valeur}</dd>
    </div>
  );
}

/**
 * Les champs d'un participant, avec le type d'entrée qui va avec.
 *
 * L'ordre est celui du classeur, pas celui du modèle : le conseiller corrige en
 * ayant le fichier sous les yeux, et lui demander de chercher « division » entre
 * « salaire » et « fumeur » serait lui faire perdre son temps.
 */
const CHAMPS: ReadonlyArray<{ champ: string; type: "text" | "date" | "number" | "email" | "tel" }> =
  [
    { champ: "employee_number", type: "text" },
    { champ: "first_name", type: "text" },
    { champ: "last_name", type: "text" },
    { champ: "mobile_phone", type: "tel" },
    { champ: "email", type: "email" },
    { champ: "birth_date", type: "date" },
    { champ: "gender", type: "text" },
    { champ: "hire_date", type: "date" },
    { champ: "hours_per_week", type: "number" },
    { champ: "smoker", type: "text" },
    { champ: "additional_email", type: "email" },
    { champ: "additional_phone", type: "tel" },
    { champ: "note", type: "text" },
    { champ: "coverage_start", type: "date" },
    { champ: "coverage_end", type: "date" },
    { champ: "employment_status", type: "text" },
    { champ: "coverage", type: "text" },
    { champ: "coverage_choice", type: "text" },
    { champ: "division", type: "text" },
    { champ: "occupation", type: "text" },
    { champ: "annual_salary", type: "number" },
    { champ: "hourly_rate", type: "number" },
    { champ: "employment_type", type: "text" },
  ];

function LigneRevue({
  ligne,
  ecartee,
  anomalies,
  correction,
  onBasculer,
  onCorriger,
}: {
  ligne: CensusImportRow;
  ecartee: boolean;
  anomalies: number;
  correction: Record<string, string | number | null> | undefined;
  onBasculer: (ligne: number) => void;
  onCorriger: (ligne: number, champ: string, valeur: string | number | null) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [ouvert, setOuvert] = useState(false);
  const v = { ...ligne.valeurs, ...(correction ?? {}) };
  const nom = [v.first_name, v.last_name].filter(Boolean).join(" ") || "—";
  const corrigee = correction !== undefined && Object.keys(correction).length > 0;
  const champsSignales = new Set(
    ligne.issues.map((i) => i.field).filter((f): f is string => f !== null),
  );

  return (
    <>
      <tr className={`border-t border-slate-100 ${ecartee ? "opacity-40" : ""}`}>
        <td className="py-1.5 pr-3">
          <input
            type="checkbox"
            checked={!ecartee}
            onChange={() => onBasculer(ligne.ligne)}
            aria-label={t("census.import.keep_row", { line: ligne.ligne, name: nom })}
            className="focus-visible:ring-brand-500 h-4 w-4 touch-manipulation rounded border-slate-300 focus-visible:outline-none focus-visible:ring-2"
          />
        </td>
        <td className="py-1.5 pr-3 tabular-nums text-slate-500">{ligne.ligne}</td>
        <td className="py-1.5 pr-3">
          <span className="break-words">{nom}</span>
          {v.employee_number ? (
            <span className="ml-1 tabular-nums text-slate-400">· {v.employee_number}</span>
          ) : null}
        </td>
        <td className="py-1.5 pr-3 tabular-nums">{v.birth_date ?? "—"}</td>
        <td className="py-1.5 pr-3 tabular-nums">
          {v.annual_salary !== null && v.annual_salary !== undefined ? (
            v.annual_salary
          ) : v.hourly_rate ? (
            <>
              {v.hourly_rate} / h
              {ligne.annual_salary_suggestion !== null ? (
                // La proposition, en gris, à CÔTÉ de la valeur. Elle n'entre dans
                // aucun champ tant qu'une personne ne l'a pas reprise à son compte.
                <span className="ml-1 text-slate-400">
                  · {t("census.import.suggested", { amount: ligne.annual_salary_suggestion })}
                </span>
              ) : null}
            </>
          ) : (
            "—"
          )}
        </td>
        <td className="py-1.5 pr-3">
          {ligne.issues.length === 0 && anomalies === 0 ? (
            <span className="text-slate-400">—</span>
          ) : (
            <span className="text-amber-700">
              {ligne.issues.length > 0
                ? t("census.import.row_issues", { count: ligne.issues.length })
                : null}
              {ligne.issues.length > 0 && anomalies > 0 ? " · " : null}
              {anomalies > 0 ? t("census.import.row_anomalies", { count: anomalies }) : null}
              {/* Les manques restent CEUX DU SERVEUR. L'écran dit que les
                  valeurs ont changé, il ne prétend pas rejuger la ligne — le
                  verdict revient de la liste après l'import. */}
              {corrigee ? (
                <span className="ml-1 text-slate-400"> {t("census.import.before_fix")}</span>
              ) : null}
            </span>
          )}
        </td>
        <td className="py-1.5">
          <button
            type="button"
            onClick={() => setOuvert((o) => !o)}
            aria-expanded={ouvert}
            aria-controls={`census-import-fix-${ligne.ligne}`}
            className="focus-visible:ring-brand-500 touch-manipulation rounded px-1 py-0.5 font-medium text-slate-600 underline decoration-dotted underline-offset-2 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2"
          >
            {ouvert ? t("census.import.close_fix") : t("census.import.fix")}
          </button>
        </td>
      </tr>
      {ouvert ? (
        <tr className="border-t border-slate-100 bg-slate-50">
          <td colSpan={7} className="px-3 py-3">
            <fieldset id={`census-import-fix-${ligne.ligne}`}>
              <legend className="sr-only">
                {t("census.import.fix_legend", { line: ligne.ligne, name: nom })}
              </legend>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {CHAMPS.map(({ champ, type }) => {
                  const id = `census-import-${ligne.ligne}-${champ}`;
                  const signale = champsSignales.has(champ);
                  // La note montre TOUJOURS ce que le classeur contenait, pas la
                  // valeur courante : c'est la provenance qui est utile, et elle
                  // reste vraie après que le conseiller a saisi une correction.
                  const brut = ligne.valeurs[champ];
                  if (champ === "coverage_choice") {
                    const parsed = Schemas.CensusCoverageChoiceSchema.safeParse(v[champ]);
                    const unknown =
                      brut != null && !Schemas.CensusCoverageChoiceSchema.safeParse(brut).success;
                    return (
                      <div key={champ}>
                        <CoverageChoice
                          id={id}
                          value={parsed.success ? parsed.data : ""}
                          readOnly={false}
                          error={null}
                          onChange={(choice) => onCorriger(ligne.ligne, champ, choice || null)}
                          onBlur={() => undefined}
                        />
                        {unknown ? (
                          <p className="mt-1 break-words text-xs text-amber-700">
                            {t("census.import.imported_value", { value: String(brut) })}
                          </p>
                        ) : null}
                      </div>
                    );
                  }
                  const illisible = !affichableDans(type, brut);
                  return (
                    <div key={champ}>
                      <label htmlFor={id} className="block text-xs font-medium text-slate-600">
                        {t(`census.fields.${champ}`)}
                        {/* L'espace est DANS le texte, pas seulement dans la
                            marge : un lecteur d'écran lit le nom accessible,
                            qui ignore le CSS — « Couvertureà vérifier » sinon. */}
                        {signale ? (
                          <span className="ml-1 font-normal text-amber-700">
                            {" "}
                            {t("census.import.flagged")}
                          </span>
                        ) : null}
                      </label>
                      <input
                        id={id}
                        type={type}
                        inputMode={type === "number" ? "decimal" : undefined}
                        step={type === "number" ? "any" : undefined}
                        // Un nom d'employé n'est pas une adresse de livraison :
                        // rien ici ne doit être proposé par le gestionnaire de
                        // formulaires du navigateur.
                        autoComplete="off"
                        spellCheck={false}
                        // Ne jamais pousser dans le champ une valeur qu'il
                        // rejetterait : elle serait silencieusement blanchie.
                        // La valeur RÉELLE, elle, n'est pas touchée — elle part
                        // telle quelle si personne ne la corrige.
                        value={
                          v[champ] === null ||
                          v[champ] === undefined ||
                          !affichableDans(type, v[champ])
                            ? ""
                            : String(v[champ])
                        }
                        aria-describedby={illisible ? `${id}-brut` : undefined}
                        onChange={(e) =>
                          onCorriger(ligne.ligne, champ, lireChamp(type, e.target.value))
                        }
                        className="focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2"
                      />
                      {illisible ? (
                        <p id={`${id}-brut`} className="mt-1 break-words text-xs text-amber-700">
                          {t("census.import.imported_value", { value: String(brut) })}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </fieldset>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * Sépare, pour une ligne, ce que le modèle sait typer de ce qu'il ne sait pas.
 *
 * Une valeur que le champ ne pourrait pas représenter — « 12 avril 85 » dans une
 * colonne `date` — ne part pas dans le champ typé, où la base la refuserait :
 * elle part dans `import_raw`, et le champ reste `null`. **Rien n'est deviné, et
 * rien n'est perdu.**
 *
 * Une correction du conseiller retire la clé correspondante : le champ est
 * réparé, donc il n'y a plus rien à conserver de ce que le classeur disait pour
 * CE champ — ce qui fait aussi disparaître le manque associé.
 */
function construireLigne(
  r: CensusImportRow,
  correction: Record<string, string | number | null> | undefined,
): Record<string, unknown> {
  const fusion = { ...r.valeurs, ...(correction ?? {}) };
  const ligne: Record<string, unknown> = {};
  const brut: Record<string, string> = {};

  for (const { champ, type } of CHAMPS) {
    const v = fusion[champ];
    if (
      v !== null &&
      v !== undefined &&
      v !== "" &&
      (!affichableDans(type, v) ||
        (champ === "coverage_choice" && !Schemas.CensusCoverageChoiceSchema.safeParse(v).success))
    ) {
      brut[champ] = String(v);
      ligne[champ] = null;
    } else {
      ligne[champ] = v ?? null;
    }
  }
  // Omis plutôt que vide : une clé absente et un objet vide diraient la même
  // chose de deux façons, et la base refuse le second.
  if (Object.keys(brut).length > 0) ligne.import_raw = brut;
  return ligne;
}

/**
 * Une valeur est-elle représentable **telle quelle** dans ce type de champ ?
 *
 * Un `<input type="date">` refuse toute valeur qui n'est pas `AAAA-MM-JJ` et se
 * rend **vide** — le navigateur comme jsdom. Une date illisible venue du
 * classeur (« 12 avril 85 ») disparaissait donc de l'écran de correction alors
 * qu'elle était intacte dans les données, et qu'elle aurait été écrite telle
 * quelle. Un champ vide qui cache une valeur non vide est pire qu'inutile : il
 * laisse croire qu'il n'y a rien à décider.
 *
 * Même piège sur `type="number"` : un salaire écrit « 62 000 $ » serait conservé
 * côté données et invisible côté champ.
 */
function affichableDans(type: string, v: unknown): boolean {
  if (v === null || v === undefined || v === "") return true;
  if (type === "date") return /^\d{4}-\d{2}-\d{2}$/.test(String(v));
  if (type === "number") return typeof v === "number" || Number.isFinite(Number(String(v).trim()));
  return true;
}

/**
 * Ce qui part vers le serveur pour un champ édité.
 *
 * Un champ vidé rend `null`, pas `""` : la base distingue « pas de valeur » de
 * « valeur vide », et les manques du serveur se calculent sur cette
 * distinction. Un nombre illisible reste **la chaîne saisie** — le contrat Zod
 * la refusera avec un message, plutôt que de la transformer en `NaN` silencieux.
 */
function lireChamp(
  type: "text" | "date" | "number" | "email" | "tel",
  brut: string,
): string | number | null {
  const t = brut.trim();
  if (t === "") return null;
  if (type !== "number") return t;
  const n = Number(t);
  return Number.isFinite(n) ? n : t;
}

/**
 * Le message à montrer, **jamais vide**.
 *
 * `getApiErrorMessage` rend `""` quand la charge d'erreur ne porte pas de
 * `messages` bilingues — et depuis 9.K.16 l'alias `message` a disparu du fil.
 * Un dialogue qui n'affiche rien du tout après un échec est pire qu'un message
 * générique : la personne ne sait même pas que quelque chose a échoué.
 */
function messageDErreur(err: unknown, lang: "fr" | "en", t: (k: string) => string): string {
  const m = getApiErrorMessage(err, lang);
  return m.length > 0 ? m : t("census.import.error_generic");
}

/** La langue courante, ramenée aux deux que le registre d'erreurs connaît. */
function langue(brut: string): "fr" | "en" {
  return brut.startsWith("en") ? "en" : "fr";
}
